import React, { useState, useEffect, useMemo } from "react";
import { Box, Button, Stack, Typography, IconButton } from "@mui/material";
import { RoleFilter, UserProfileFilter } from "@/pages/user/types";
import { IconAdd, IconArrowDropDown, IconFilterWithPlus } from "@/icons";
import SearchFilter from "./SearchFilter";
import StatusFilter from "./StatusFilter";
import RoleFilterPill from "./RoleFilterPill";
import DateFilter from "./DateFilter";
import FilterMenu from "./FilterMenu";
import { roleDefinitions } from "../../roleDefinitions";

export type FilterOption = {
  id: string;
  label: string;
  component: React.FC<FilterComponentProps>;
  props?: Record<string, any>; // Additional props to pass to the component
};

export interface FilterComponentProps {
  onChange: (value: any) => void;
  onRemove: () => void;
  value: any;
  organizationId?: string;
  courseId?: string;
  roleType?: string;
}

interface UserProfileFiltersProps {
  filters: UserProfileFilter;
  onChange: (filters: UserProfileFilter) => void;
  organizationId?: string;
  courseId?: string;
}

export const FILTER_OPTIONS: FilterOption[] = [
  {
    id: "status",
    label: "Status",
    component: StatusFilter,
  },
  {
    id: "roles",
    label: "Roles",
    component: RoleFilterPill,
  },
  {
    id: "dateAdded",
    label: "Date Added",
    component: DateFilter,
    props: { label: "Date Added" },
  },
  {
    id: "dateLastActive",
    label: "Last Active",
    component: DateFilter,
    props: { label: "Last Active" },
  },
];

// Generate a unique ID for each filter instance
const generateFilterId = () =>
  `filter_${Math.random().toString(36).substr(2, 9)}`;

const UserProfileFilters: React.FC<UserProfileFiltersProps> = ({
  filters,
  onChange,
  organizationId,
  courseId,
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [activeFilters, setActiveFilters] = useState<
    { id: string; type: string; props?: Record<string, any> }[]
  >([]);

  // Track active filter types to prevent duplicates
  const activeFilterTypes = useMemo(() => {
    const types = activeFilters.map((filter) => filter.type);
    return [...new Set(types)];
  }, [activeFilters]);

  // Get available role types based on context
  const availableRoleTypes = useMemo(() => {
    // In course context, only show course roles
    if (courseId) {
      return Object.entries(roleDefinitions)
        .filter(([_, def]) => def.scope === "course")
        .map(([id]) => id);
    }

    // In org context, show org and course roles
    if (organizationId) {
      return Object.entries(roleDefinitions)
        .filter(
          ([_, def]) => def.scope === "organization" || def.scope === "course"
        )
        .map(([id]) => id);
    }

    // In global context, show all roles
    return Object.keys(roleDefinitions);
  }, [organizationId, courseId]);

  // Initialize activeFilters based on current filters
  useEffect(() => {
    const initialFilters: {
      id: string;
      type: string;
      props?: Record<string, any>;
    }[] = [];

    // Add status filter if present
    if (filters.status?.length) {
      initialFilters.push({ id: generateFilterId(), type: "status" });
    }

    // Add date filters if present
    if (filters.dateAdded) {
      initialFilters.push({ id: generateFilterId(), type: "dateAdded" });
    }

    if (filters.dateLastActive) {
      initialFilters.push({ id: generateFilterId(), type: "dateLastActive" });
    }

    // Add role filters
    if (filters.roles?.length) {
      filters.roles.forEach((roleFilter) => {
        initialFilters.push({
          id: generateFilterId(),
          type: "roles",
          props: { roleType: roleFilter.roleType },
        });
      });
    }

    if (initialFilters.length > 0) {
      setActiveFilters(initialFilters);
    }
  }, []);

  // Always show search, then show active filters
  const handleSearchChange = (searchValue: string) => {
    onChange({
      ...filters,
      search: searchValue,
    });
  };

  const handleAddFilter = (
    filterType: string,
    additionalProps?: Record<string, any>
  ) => {
    // For status and date filters, don't allow duplicates
    if (filterType !== "roles" && activeFilterTypes.includes(filterType)) {
      // Filter already exists - close menu but don't add another one
      setMenuAnchorEl(null);
      return;
    }

    const newFilterId = generateFilterId();
    setActiveFilters([
      ...activeFilters,
      { id: newFilterId, type: filterType, props: additionalProps },
    ]);
    setMenuAnchorEl(null);

    // Initialize the filter value in the parent component
    if (filterType === "roles" && additionalProps?.roleType) {
      const newRoleFilter: RoleFilter = {
        roleType: additionalProps.roleType,
        // If in context, add the context values
        ...(organizationId && { organizationId }),
        ...(courseId && { courseId }),
      };

      onChange({
        ...filters,
        roles: [...(filters.roles || []), newRoleFilter],
      });
    }
  };

  const handleRemoveFilter = (filterId: string) => {
    const filterToRemove = activeFilters.find((f) => f.id === filterId);
    if (!filterToRemove) return;

    setActiveFilters(activeFilters.filter((f) => f.id !== filterId));

    // Remove the filter value from the filters object
    const newFilters = { ...filters };

    if (filterToRemove.type === "roles") {
      // Find the index of the role filter to remove
      const roleIndex = activeFilters
        .filter((f) => f.type === "roles")
        .findIndex((f) => f.id === filterId);

      if (roleIndex !== -1 && newFilters.roles) {
        newFilters.roles = [
          ...newFilters.roles.slice(0, roleIndex),
          ...newFilters.roles.slice(roleIndex + 1),
        ];

        if (newFilters.roles.length === 0) {
          delete newFilters.roles;
        }
      }
    } else {
      delete newFilters[filterToRemove.type as keyof UserProfileFilter];
    }

    onChange(newFilters);
  };

  const handleFilterChange = (filterId: string, value: any) => {
    const filter = activeFilters.find((f) => f.id === filterId);
    if (!filter) return;

    if (filter.type === "roles") {
      // Find the index of the role filter to update
      const roleIndex = activeFilters
        .filter((f) => f.type === "roles")
        .findIndex((f) => f.id === filterId);

      if (roleIndex !== -1) {
        const newRoles = [...(filters.roles || [])];
        newRoles[roleIndex] = value;

        onChange({
          ...filters,
          roles: newRoles,
        });
      }
    } else {
      onChange({
        ...filters,
        [filter.type]: value,
      });
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  // Filter out options that are not applicable in the current context
  const availableFilters = FILTER_OPTIONS.filter((option) => {
    // Don't show organization-specific filters if we're in org context
    if (option.id === "organizationIds" && organizationId) {
      return false;
    }

    // Don't show course-specific filters if we're in course context
    if (option.id === "courseIds" && (courseId || organizationId)) {
      return false;
    }

    return true;
  });

  return (
    <Stack spacing={2}>
      {/* Filters container - horizontal layout with wrap */}
      <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
        {/* Search is always shown */}
        <SearchFilter
          value={filters.search || ""}
          onChange={handleSearchChange}
          onRemove={() => {}} // Search can't be removed
        />

        {/* Active filters */}
        {activeFilters.map((filter) => {
          const filterOption = FILTER_OPTIONS.find(
            (opt) => opt.id === filter.type
          );
          if (!filterOption) return null;

          const FilterComponent = filterOption.component;
          const roleIndex =
            filter.type === "roles"
              ? activeFilters
                  .filter((f) => f.type === "roles")
                  .findIndex((f) => f.id === filter.id)
              : -1;

          const filterValue =
            filter.type === "roles" && filters.roles && roleIndex !== -1
              ? filters.roles[roleIndex]
              : filters[filter.type as keyof UserProfileFilter];

          return (
            <FilterComponent
              key={filter.id}
              value={filterValue}
              onChange={(value) => handleFilterChange(filter.id, value)}
              onRemove={() => handleRemoveFilter(filter.id)}
              {...(filterOption.props || {})}
              {...(filter.props || {})}
              organizationId={organizationId}
              courseId={courseId}
            />
          );
        })}

        {/* Add filter button */}
        <Button
          onClick={handleMenuOpen}
          variant="contained"
          color="secondary"
          size="small"
          endIcon={<IconArrowDropDown />}
        ><IconFilterWithPlus />
        </Button>
      </Box>

      <FilterMenu
        anchorEl={menuAnchorEl}
        onClose={handleMenuClose}
        filterOptions={availableFilters}
        activeFilterTypes={activeFilterTypes}
        onSelect={handleAddFilter}
        availableRoleTypes={availableRoleTypes}
      />
    </Stack>
  );
};

export default UserProfileFilters;
